import { HttpClient, robotoHeaders, RobotoNotFoundException } from "../../http";

import { RobotoUserConfigKey } from "./ConfigRecords";

interface Options {
  abortSignal: AbortSignal;
}

interface GetConfigResponse {
  key: string;
  value: string;
}

interface SetConfigRequest {
  value: string;
}

export class ConfigService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getOrgConfig(
    orgId: string,
    key: string,
    options?: Partial<Options>,
  ): Promise<string | null> {
    try {
      const requestUrl = this.#httpClient.constructUrl(
        `v1/config/org/${orgId}/key/${encodeURIComponent(key)}`,
      );
      const response = await this.#httpClient.get(requestUrl, {
        signal: options?.abortSignal,
        headers: robotoHeaders({ orgId }),
      });
      const data = await response.json<GetConfigResponse>();
      return data.value;
    } catch (error) {
      // If config value doesn't exist (404), return null
      if (error instanceof RobotoNotFoundException) {
        return null;
      }
      throw error;
    }
  }

  public async setOrgConfig(
    orgId: string,
    key: string,
    value: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/config/org/${orgId}/key/${encodeURIComponent(key)}`,
    );
    const request: SetConfigRequest = { value };

    await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
  }

  public async getUserConfig(
    key: RobotoUserConfigKey,
    options?: Partial<Options>,
  ): Promise<string | null> {
    try {
      const requestUrl = this.#httpClient.constructUrl(
        `v1/config/user/key/${encodeURIComponent(key)}`,
      );
      const response = await this.#httpClient.get(requestUrl, {
        signal: options?.abortSignal,
      });
      const data = await response.json<GetConfigResponse>();
      return data.value;
    } catch (error) {
      // If config value doesn't exist (404), return null
      if (error instanceof RobotoNotFoundException) {
        return null;
      }
      throw error;
    }
  }

  public async setUserConfig(
    key: RobotoUserConfigKey,
    value: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/config/user/key/${encodeURIComponent(key)}`,
    );
    const request: SetConfigRequest = { value };

    await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
  }
}
